import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  MapPin, Navigation, Clock, Gauge, Activity, User, Bike, 
  RefreshCw, Loader2, AlertCircle, CheckCircle2, XCircle,
  Calendar, Route, Timer, Zap, Link as LinkIcon, Play, Square,
  ChevronDown, ChevronUp, Car
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatErrorMessage } from '../utils/formatError';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create numbered stop marker
const createStopIcon = (number) => new L.DivIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: #3B82F6; width: 24px; height: 24px; border-radius: 4px; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); font-size: 11px; font-weight: bold; color: white;">
    ${number}
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

// Custom motorcycle icons with transparent backgrounds (provided by user)
const BIKE_ICON_RED = 'https://customer-assets.emergentagent.com/job_bike-gps-match/artifacts/ljbh3d4o_Icon_final_SR_350X_with_Rider-removebg-preview.png';
const BIKE_ICON_GREY = 'https://customer-assets.emergentagent.com/job_bike-gps-match/artifacts/deko3326_Final_icon_grey_SR_350X_with_rider-removebg-preview.png';

// Running motorcycle icon (red bike) - for bikes that are moving
const motorcycleIcon = new L.DivIcon({
  className: 'bike-marker-running',
  html: `<div style="
    width: 55px;
    height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(2px 3px 4px rgba(0,0,0,0.5));
  ">
    <img src="${BIKE_ICON_RED}" style="
      width: 55px;
      height: 55px;
      object-fit: contain;
    " />
  </div>`,
  iconSize: [55, 55],
  iconAnchor: [27, 55],
  popupAnchor: [0, -55],
});

// Idle motorcycle icon (red bike INSIDE orange circle) - for stationary but communicating bikes
const idleIcon = new L.DivIcon({
  className: 'bike-marker-idle',
  html: `<div style="
    width: 65px;
    height: 65px;
    background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 4px 10px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  ">
    <img src="${BIKE_ICON_RED}" style="
      width: 48px;
      height: 48px;
      object-fit: contain;
    " />
  </div>`,
  iconSize: [71, 71],
  iconAnchor: [35, 71],
  popupAnchor: [0, -71],
});

// Offline motorcycle icon (grey bike) - for bikes not communicating
const offlineIcon = new L.DivIcon({
  className: 'bike-marker-offline',
  html: `<div style="
    width: 55px;
    height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(2px 3px 4px rgba(0,0,0,0.5));
  ">
    <img src="${BIKE_ICON_GREY}" style="
      width: 55px;
      height: 55px;
      object-fit: contain;
    " />
  </div>`,
  iconSize: [55, 55],
  iconAnchor: [27, 55],
  popupAnchor: [0, -55],
});

// Component to update map center when location changes
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Component to fit map bounds to route data
const MapRouteUpdater = ({ routeData }) => {
  const map = useMap();
  useEffect(() => {
    if (routeData && routeData.length > 0) {
      const bounds = L.latLngBounds(routeData.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [routeData, map]);
  return null;
};

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BikeTrackingDialog = ({ open, onOpenChange, motorcycle, getAuthHeader }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [gofleetDevices, setGofleetDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [linkingMode, setLinkingMode] = useState(false);
  const [activeTab, setActiveTab] = useState('live');
  const [tripDateRange, setTripDateRange] = useState('this_week');
  const [tripsLoading, setTripsLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [tripStats, setTripStats] = useState({ totalDistance: 0, totalDuration: 0, totalTrips: 0, avgSpeed: 0 });
  const [routeData, setRouteData] = useState([]);  // GPS route points for the map
  const [routeLoading, setRouteLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});  // Track which day groups are expanded
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const mapRef = useRef(null);
  const autoRefreshIntervalRef = useRef(null);

  // Auto-refresh interval (30 seconds)
  const AUTO_REFRESH_INTERVAL = 30000;

  // Date range options
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
  ];

  // Calculate date range based on selection
  const getDateRange = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case 'today':
        return { from: today.toISOString(), to: now.toISOString() };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: yesterday.toISOString(), to: today.toISOString() };
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - 7);
        return { from: weekStart.toISOString(), to: now.toISOString() };
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: monthStart.toISOString(), to: now.toISOString() };
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: lastMonthStart.toISOString(), to: lastMonthEnd.toISOString() };
      default:
        const defaultStart = new Date(today);
        defaultStart.setDate(defaultStart.getDate() - 7);
        return { from: defaultStart.toISOString(), to: now.toISOString() };
    }
  };

  // Fetch tracking data when dialog opens
  useEffect(() => {
    if (open && motorcycle?.id) {
      fetchTrackingData();
    }
  }, [open, motorcycle?.id]);

  // Auto-refresh every 30 seconds when dialog is open and on Live Location tab
  useEffect(() => {
    if (open && autoRefreshEnabled && activeTab === 'live' && !linkingMode) {
      // Clear any existing interval
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      
      // Set up new interval
      autoRefreshIntervalRef.current = setInterval(() => {
        fetchTrackingData(true); // true = silent refresh (no loading spinner)
      }, AUTO_REFRESH_INTERVAL);
      
      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
        }
      };
    }
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [open, autoRefreshEnabled, activeTab, linkingMode]);

  // Fetch trips when date range changes
  useEffect(() => {
    if (open && trackingData?.motorcycle?.gofleet_device_id && activeTab === 'trips') {
      setExpandedDays({});
      fetchTrips();
    }
  }, [tripDateRange, activeTab, trackingData?.motorcycle?.gofleet_device_id]);

  const fetchTrackingData = async (silentRefresh = false) => {
    if (!motorcycle?.id) return;
    
    if (!silentRefresh) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    
    try {
      const response = await axios.get(
        `${API}/gofleet/motorcycle/${motorcycle.id}/tracking`,
        { headers: getAuthHeader() }
      );
      setTrackingData(response.data);
      setLastRefresh(new Date());
      
      // If no device linked, show linking mode
      if (response.data.error === "No GoFleet device linked to this motorcycle") {
        setLinkingMode(true);
        fetchGofleetDevices();
      } else {
        setLinkingMode(false);
        // Fetch trips with initial date range
        if (response.data.motorcycle?.gofleet_device_id) {
          fetchTripsForDevice(response.data.motorcycle.gofleet_device_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tracking data:', error);
      toast.error('Failed to fetch tracking data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrips = async () => {
    if (!trackingData?.motorcycle?.gofleet_device_id) return;
    await fetchTripsForDevice(trackingData.motorcycle.gofleet_device_id);
  };

  const fetchTripsForDevice = async (deviceId) => {
    setTripsLoading(true);
    try {
      const { from, to } = getDateRange(tripDateRange);
      const response = await axios.get(
        `${API}/gofleet/devices/${deviceId}/trips?from_date=${from}&to_date=${to}`,
        { headers: getAuthHeader() }
      );
      
      const tripsData = response.data.trips || [];
      setTrips(tripsData);
      
      // Calculate stats
      const totalDistance = tripsData.reduce((sum, trip) => sum + (trip.distance || 0), 0);
      const totalDurationSec = tripsData.reduce((sum, trip) => {
        if (typeof trip.duration === 'string' && trip.duration.includes(':')) {
          const parts = trip.duration.split(':');
          return sum + (parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + (parseInt(parts[2]) || 0));
        }
        return sum + (parseInt(trip.duration) || 0);
      }, 0);
      const avgSpeed = tripsData.length > 0 
        ? tripsData.reduce((sum, trip) => sum + (trip.average_speed || 0), 0) / tripsData.length 
        : 0;
      
      setTripStats({
        totalDistance,
        totalDuration: totalDurationSec,
        totalTrips: tripsData.length,
        avgSpeed
      });
      
      // Fetch route data for the date range
      await fetchRouteData(deviceId, from, to);
    } catch (error) {
      console.error('Failed to fetch trips:', error);
      setTrips([]);
    } finally {
      setTripsLoading(false);
    }
  };

  const fetchRouteData = async (deviceId, fromDate, toDate) => {
    setRouteLoading(true);
    try {
      const response = await axios.get(
        `${API}/gofleet/devices/${deviceId}/trip-route?from_date=${fromDate}&to_date=${toDate}`,
        { headers: getAuthHeader() }
      );
      
      const points = response.data.route || [];
      // Downsample if too many points (for performance)
      const maxPoints = 500;
      if (points.length > maxPoints) {
        const step = Math.ceil(points.length / maxPoints);
        const downsampled = points.filter((_, idx) => idx % step === 0);
        setRouteData(downsampled);
      } else {
        setRouteData(points);
      }
    } catch (error) {
      console.error('Failed to fetch route data:', error);
      setRouteData([]);
    } finally {
      setRouteLoading(false);
    }
  };

  const fetchGofleetDevices = async () => {
    try {
      const response = await axios.get(`${API}/gofleet/devices`, { headers: getAuthHeader() });
      setGofleetDevices(response.data.devices || []);
    } catch (error) {
      console.error('Failed to fetch GoFleet devices');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTrackingData();
    setRefreshing(false);
    toast.success('Tracking data refreshed');
  };

  const handleLinkDevice = async () => {
    if (!selectedDeviceId) {
      toast.error('Please select a GoFleet device');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/gofleet/link-motorcycle/${motorcycle.id}?gofleet_device_id=${selectedDeviceId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Motorcycle linked to GoFleet device');
      setLinkingMode(false);
      fetchTrackingData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to link device'));
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return '0m';
    
    // Handle string format "H:M:S" from GoFleet API
    if (typeof duration === 'string' && duration.includes(':')) {
      const parts = duration.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]) || 0;
        const minutes = parseInt(parts[1]) || 0;
        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
      }
    }
    
    // Handle seconds format
    const seconds = typeof duration === 'number' ? duration : parseInt(duration);
    if (isNaN(seconds)) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatDistance = (km) => {
    if (!km) return '0 km';
    return `${km.toFixed(1)} km`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusIcon = () => {
    if (!trackingData?.tracking) return <XCircle className="w-5 h-5 text-red-500" />;
    if (trackingData.tracking.is_running) return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    if (trackingData.tracking.is_idle) return <Clock className="w-5 h-5 text-amber-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getStatusText = () => {
    if (!trackingData?.tracking) return 'Offline';
    if (trackingData.tracking.is_running) return 'Running';
    if (trackingData.tracking.is_idle) return 'Idle';
    return trackingData.tracking.status || 'Unknown';
  };

  const getStatusColor = () => {
    if (!trackingData?.tracking) return 'bg-red-100 text-red-700';
    if (trackingData.tracking.is_running) return 'bg-green-100 text-green-700';
    if (trackingData.tracking.is_idle) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getMarkerIcon = () => {
    if (!trackingData?.tracking) return offlineIcon;
    if (trackingData.tracking.is_running) return motorcycleIcon;
    if (trackingData.tracking.is_idle) return idleIcon;
    return offlineIcon;
  };

  const mapCenter = trackingData?.tracking?.latitude && trackingData?.tracking?.longitude
    ? [trackingData.tracking.latitude, trackingData.tracking.longitude]
    : [25.2048, 55.2708]; // Default to Dubai

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Navigation className="w-6 h-6 text-blue-600" />
            Live Tracking - {motorcycle?.plate_number}
          </DialogTitle>
          <DialogDescription>
            Real-time GPS tracking, trip history, and vehicle status
          </DialogDescription>
        </DialogHeader>

        {loading && !trackingData ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-lg">Loading tracking data...</span>
          </div>
        ) : linkingMode ? (
          /* Device Linking Mode */
          <div className="space-y-6 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">No GoFleet device linked to this motorcycle</span>
              </div>
              <p className="text-sm text-amber-700 mt-2">
                Please select a GoFleet device to link with this motorcycle for GPS tracking.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bike className="w-4 h-4" />
                    Motorcycle Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p><span className="text-slate-500">Plate:</span> {motorcycle?.plate_number}</p>
                  <p><span className="text-slate-500">Brand:</span> {motorcycle?.brand || '-'}</p>
                  <p><span className="text-slate-500">Model:</span> {motorcycle?.model_name}</p>
                  <p><span className="text-slate-500">Color:</span> {motorcycle?.color}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    Link GoFleet Device
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a GoFleet device..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {gofleetDevices.map(device => (
                        <SelectItem key={device.id} value={device.id}>
                          <div className="flex flex-col">
                            <span>{device.name || device.license_plate || device.id}</span>
                            {device.license_plate && (
                              <span className="text-xs text-slate-500">Plate: {device.license_plate}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleLinkDevice} disabled={!selectedDeviceId || loading} className="w-full gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
                    Link Device
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* Main Tracking View */
          <div className="space-y-4">
            {/* Header Stats */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge className={`${getStatusColor()} px-3 py-1 text-sm flex items-center gap-2`}>
                  {getStatusIcon()}
                  {getStatusText()}
                </Badge>
                {trackingData?.tracking?.speed > 0 && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Gauge className="w-4 h-4" />
                    {trackingData.tracking.speed} km/h
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Auto-refresh indicator */}
                {autoRefreshEnabled && !linkingMode && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Auto-refresh: 30s</span>
                    {lastRefresh && (
                      <span className="text-slate-400">
                        | Last: {lastRefresh.toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-2">
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Bike className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Motorcycle</p>
                      <p className="font-semibold">{trackingData?.motorcycle?.plate_number}</p>
                      <p className="text-xs text-slate-500">{trackingData?.motorcycle?.brand} {trackingData?.motorcycle?.model_name}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Assigned Rider</p>
                      <p className="font-semibold">{trackingData?.rider?.name || 'Not Assigned'}</p>
                      {trackingData?.rider?.mobile_uae && (
                        <p className="text-xs text-slate-500">{trackingData.rider.mobile_uae}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Odometer</p>
                      <p className="font-semibold">{formatDistance(trackingData?.tracking?.odometer)}</p>
                      <p className="text-xs text-slate-500">Total distance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Last Update</p>
                      <p className="font-semibold text-sm">
                        {trackingData?.tracking?.last_communication 
                          ? new Date(trackingData.tracking.last_communication).toLocaleTimeString()
                          : '-'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {trackingData?.tracking?.last_communication 
                          ? new Date(trackingData.tracking.last_communication).toLocaleDateString()
                          : '-'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="live" className="gap-2">
                  <MapPin className="w-4 h-4" />
                  Live Location
                </TabsTrigger>
                <TabsTrigger value="trips" className="gap-2">
                  <Route className="w-4 h-4" />
                  Trip History ({trips.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="live" className="mt-4">
                {/* Map */}
                <div className="h-[400px] rounded-lg overflow-hidden border">
                  {trackingData?.tracking?.latitude && trackingData?.tracking?.longitude ? (
                    <MapContainer
                      center={mapCenter}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                      ref={mapRef}
                    >
                      <TileLayer
                        attribution='Map data &copy; Google'
                        url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                        maxZoom={20}
                      />
                      <MapUpdater center={mapCenter} />
                      <Marker position={mapCenter} icon={getMarkerIcon()}>
                        <Popup>
                          <div className="text-sm">
                            <p className="font-semibold">{trackingData?.motorcycle?.plate_number}</p>
                            <p>Status: {getStatusText()}</p>
                            <p>Speed: {trackingData?.tracking?.speed || 0} km/h</p>
                            <p>Last update: {formatDateTime(trackingData?.tracking?.last_communication)}</p>
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-slate-100">
                      <div className="text-center text-slate-500">
                        <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No location data available</p>
                        <p className="text-sm">The device may be offline or has no GPS signal</p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="trips" className="mt-4">
                {/* Date Range Selector */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <Select value={tripDateRange} onValueChange={setTripDateRange}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dateRangeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {tripsLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                  </div>
                  
                  {/* Trip Stats Summary */}
                  <div className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded">
                      <Square className="w-3 h-3 text-blue-600" />
                      <span className="text-blue-700 font-medium">{tripStats.totalTrips} Stops</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded">
                      <Navigation className="w-3 h-3 text-green-600" />
                      <span className="text-green-700 font-medium">{tripStats.totalDistance.toFixed(1)} km</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded">
                      <Car className="w-3 h-3 text-purple-600" />
                      <span className="text-purple-700 font-medium">
                        {Math.floor(tripStats.totalDuration / 3600)}h {Math.floor((tripStats.totalDuration % 3600) / 60)}m Driving
                      </span>
                    </div>
                  </div>
                </div>

                {/* GoFleet-style Trip View: Left Panel + Map */}
                {trips.length > 0 ? (
                  <div className="flex gap-4 h-[450px]">
                    {/* Left Panel - Trip Timeline */}
                    <div className="w-80 border rounded-lg bg-slate-50 flex flex-col">
                      <ScrollArea className="flex-1">
                        <div className="p-3 space-y-2">
                          {/* Group trips by date */}
                          {(() => {
                            const groupedEntries = Object.entries(
                              trips.reduce((acc, trip, idx) => {
                                const date = trip.start_time ? new Date(trip.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Unknown';
                                if (!acc[date]) acc[date] = [];
                                acc[date].push({ ...trip, index: idx + 1 });
                                return acc;
                              }, {})
                            );
                            return groupedEntries.map(([date, dateTrips], dayIdx) => {
                            const dayDistance = dateTrips.reduce((sum, t) => sum + (t.distance || 0), 0);
                            const dayDuration = dateTrips.reduce((sum, t) => {
                              if (typeof t.duration === 'string' && t.duration.includes(':')) {
                                const parts = t.duration.split(':');
                                return sum + (parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60);
                              }
                              return sum + (parseInt(t.duration) || 0);
                            }, 0);
                            // First day expanded by default, rest collapsed
                            const isExpanded = expandedDays[date] !== undefined ? expandedDays[date] : dayIdx === 0;
                            
                            return (
                              <div key={date} className="bg-white rounded-lg border overflow-hidden">
                                {/* Date Header - Clickable */}
                                <div
                                  className="bg-slate-100 px-3 py-2 border-b flex items-center justify-between cursor-pointer hover:bg-slate-200 transition-colors select-none"
                                  data-testid={`trip-day-header-${dayIdx}`}
                                  onClick={() => setExpandedDays(prev => ({ ...prev, [date]: !isExpanded }))}
                                >
                                  <div className="flex items-center gap-2">
                                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronUp className="w-4 h-4 text-slate-500" />}
                                    <Calendar className="w-4 h-4 text-slate-500" />
                                    <span className="font-medium text-sm">{date}</span>
                                  </div>
                                  <Badge variant="outline" className="text-xs">{dateTrips.length} trips</Badge>
                                </div>
                                
                                {/* Day Summary - always visible */}
                                <div className="px-3 py-2 bg-slate-50 border-b text-xs text-slate-600 flex gap-4">
                                  <span>🚗 {dayDistance.toFixed(1)} km</span>
                                  <span>⏱️ {Math.floor(dayDuration / 3600)}h {Math.floor((dayDuration % 3600) / 60)}m</span>
                                </div>
                                
                                {/* Individual Trips/Stops - collapsible */}
                                {isExpanded && (
                                <div className="divide-y">
                                  {dateTrips.map((trip, tripIdx) => (
                                    <div key={trip.id || tripIdx}>
                                      {/* Stop Entry */}
                                      <div className="px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors">
                                        <div className="flex items-start gap-2">
                                          <div className="w-6 h-6 rounded bg-blue-500 text-white text-xs flex items-center justify-center font-bold shrink-0">
                                            {trip.index}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1 text-xs text-slate-500">
                                              <Clock className="w-3 h-3" />
                                              {trip.start_time ? new Date(trip.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                            </div>
                                            <p className="text-xs text-slate-700 truncate mt-0.5">
                                              {trip.start_address || 'Location coordinates available'}
                                            </p>
                                            <div className="flex gap-3 mt-1 text-xs">
                                              <span className="text-slate-500">
                                                <Square className="w-3 h-3 inline mr-0.5" />
                                                {formatDuration(trip.idle_duration || '0:0:0')} idle
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Drive Segment */}
                                      <div className="px-3 py-2 bg-green-50/50 border-l-2 border-green-500">
                                        <div className="flex items-center gap-2">
                                          <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                                            <Play className="w-3 h-3 ml-0.5" />
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs font-medium text-green-700">Drive</span>
                                              <span className="text-xs text-slate-500">
                                                {trip.end_time ? new Date(trip.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                              </span>
                                            </div>
                                            <div className="flex gap-3 text-xs text-slate-600 mt-0.5">
                                              <span>⏱️ {formatDuration(trip.duration)}</span>
                                              <span>📍 {formatDistance(trip.distance)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                )}
                              </div>
                            );
                          });
                          })()}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    {/* Right Panel - Map with Route */}
                    <div className="flex-1 border rounded-lg overflow-hidden relative">
                      {routeLoading && (
                        <div className="absolute top-2 right-2 z-[1000] bg-white/90 px-3 py-1 rounded-full shadow text-xs flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                          Loading route...
                        </div>
                      )}
                      <MapContainer
                        center={routeData.length > 0 ? [routeData[0].lat, routeData[0].lng] : mapCenter}
                        zoom={12}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          attribution='Map data &copy; Google'
                          url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                          maxZoom={20}
                        />
                        
                        {/* Auto-fit map to route bounds */}
                        {routeData.length > 0 && <MapRouteUpdater routeData={routeData} />}
                        
                        {/* Route Polyline from GPS LogRecords */}
                        {routeData.length > 1 && (
                          <Polyline
                            positions={routeData.map(p => [p.lat, p.lng])}
                            color="#8B5CF6"
                            weight={4}
                            opacity={0.8}
                          />
                        )}
                        
                        {/* Stop Markers based on trip start/end times */}
                        {trips.slice(0, 50).map((trip, idx) => {
                          // Try to find a route point close to the trip start time
                          const tripStartTime = trip.start_time ? new Date(trip.start_time).getTime() : null;
                          let position = null;
                          
                          if (tripStartTime && routeData.length > 0) {
                            // Find the closest route point to this trip's start time
                            let closestPoint = routeData[0];
                            let minDiff = Infinity;
                            
                            for (const point of routeData) {
                              if (point.timestamp) {
                                const pointTime = new Date(point.timestamp).getTime();
                                const diff = Math.abs(pointTime - tripStartTime);
                                if (diff < minDiff) {
                                  minDiff = diff;
                                  closestPoint = point;
                                }
                              }
                            }
                            
                            if (closestPoint.lat && closestPoint.lng) {
                              position = [closestPoint.lat, closestPoint.lng];
                            }
                          }
                          
                          // Fallback to trip coordinates if available
                          if (!position && trip.start_latitude && trip.start_longitude) {
                            position = [trip.start_latitude, trip.start_longitude];
                          }
                          
                          if (!position) return null;
                          
                          return (
                            <Marker
                              key={trip.id || idx}
                              position={position}
                              icon={createStopIcon(idx + 1)}
                            >
                              <Popup>
                                <div className="text-sm">
                                  <p className="font-semibold">Stop {idx + 1}</p>
                                  <p className="text-xs text-slate-500">
                                    {trip.start_time ? new Date(trip.start_time).toLocaleString() : '-'}
                                  </p>
                                  <p className="text-xs mt-1">{trip.start_address || 'Location from GPS'}</p>
                                  <div className="mt-1 text-xs">
                                    <span>Distance: {formatDistance(trip.distance)}</span>
                                    <br />
                                    <span>Duration: {formatDuration(trip.duration)}</span>
                                  </div>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })}
                        
                        {/* Current location marker */}
                        {trackingData?.tracking?.latitude && trackingData?.tracking?.longitude && (
                          <Marker
                            position={[trackingData.tracking.latitude, trackingData.tracking.longitude]}
                            icon={getMarkerIcon()}
                          >
                            <Popup>
                              <div className="text-sm">
                                <p className="font-semibold">Current Location</p>
                                <p>Status: {getStatusText()}</p>
                              </div>
                            </Popup>
                          </Marker>
                        )}
                      </MapContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500 border rounded-lg bg-slate-50">
                    <Route className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="font-medium">No trips recorded</p>
                    <p className="text-sm mt-1">
                      {tripDateRange === 'today' ? 'No trips recorded today' :
                       tripDateRange === 'yesterday' ? 'No trips recorded yesterday' :
                       tripDateRange === 'this_week' ? 'No trips recorded this week' :
                       tripDateRange === 'this_month' ? 'No trips recorded this month' :
                       tripDateRange === 'last_month' ? 'No trips recorded last month' :
                       'No trips recorded in the selected period'}
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Error Message */}
            {trackingData?.error && !linkingMode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertCircle className="w-5 h-5" />
                  <span>{trackingData.error}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BikeTrackingDialog;
